/**
 * Utilitários para manipulação de strings
 */
import {HttpErrorResponse} from '@angular/common/http';

export class StringUtils {
  /**
   * Retorna o primeiro e o último nome como uma string única.
   * @param fullName Nome completo do usuário
   * @returns Ex: "Pablo Silva", "Ana", ""
   */
  public static getFirstAndLastName(fullName: string | null | undefined): string {
    if (!fullName) {
      return '';
    }

    const parts = fullName.trim().split(/\s+/);

    if (parts.length === 1) {
      return parts[0]; // ex: "Ana"
    }

    return `${parts[0]} ${parts.at(-1)}`; // ex: "Pablo Silva"
  }

  /**
   * Retorna true se a string contiver apenas tags HTML (sem conteúdo textual visível).
   * Útil para validar campos de rich text que podem retornar HTML vazio como "<p><br></p>".
   *
   * @param html string HTML a ser verificada
   * @returns true se não houver texto real
   */
  static isHtmlEmpty(html: string | null | undefined): boolean {
    if (!html) return true;

    let text = '';
    let index = 0;
    while (index < html.length) {
      const open = html.indexOf('<', index);
      if (open === -1) {
        text += html.slice(index);
        break;
      }
      text += html.slice(index, open);
      const close = html.indexOf('>', open + 1);
      if (close === -1) {
        text += html.slice(open);
        break;
      }
      index = close + 1;
    }

    return text.trim() === '';
  }

  /**
   * Aplica máscara de telefone para números com 10 ou 11 dígitos.
   * Retorna string vazia se null, undefined ou inválido.
   *
   * @param phone string | null | undefined
   * @returns string formatada: (11) 99999-9999 ou (11) 3333-3333
   */
  public static formatPhoneNumber(phone: string | null | undefined): string {
    if (!phone) return '';

    const digitsOnly = phone.replaceAll(/\D/g, '');

    if (digitsOnly.length === 10) {
      return digitsOnly.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else if (digitsOnly.length === 11) {
      return digitsOnly.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }

    return phone;
  }

  /**
   * Retorna as iniciais do primeiro e último nomes.
   * Ex: "Amanda Lobo Gomes" → "AG"
   */
  static getFirstAndLastInitials(fullName: string): string {
    if (!fullName) return '';

    const parts = fullName
      .trim()
      .split(/\s+/)
      .filter((p) => p.length > 0);

    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0][0].toUpperCase();

    const firstInitial = parts[0][0].toUpperCase();
    const lastInitial = parts.at(-1)![0].toUpperCase();

    return firstInitial + lastInitial;
  }


  static createErrorMessageFromResponse(error: HttpErrorResponse, fallbackMessage: string) {
    const errorMessage = error.error.message ?? null;
    if (!errorMessage) return fallbackMessage;
    let msg = errorMessage;
    if (error.error.errors && error.error.errors.length > 0) {
      msg = msg.concat('\n' + error.error.errors.join(';\n'));
    }
    return msg;
  }

  static getFormattedFileSize(bytes: number): string {
    if (bytes == null || Number.isNaN(bytes)) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  }


  /**
   * Aplica regra de exclusividade em uma lista de valores selecionados.
   *
   * - Se o valor exclusivo estiver selecionado junto com outros,
   *   ele prevalece e os demais são removidos.
   * - Se outros valores forem selecionados, o valor exclusivo é removido.
   * @param values Lista atual de valores selecionados.
   * @param exclusiveValue Valor que deve se comportar como opção exclusiva.
   * @param eventValue Valor originado do evento de seleção.
   * @returns Nova lista tratada conforme regra de exclusividade.
   */
  static handleExclusiveOption(
    values: string[],
    exclusiveValue: string,
    eventValue?: string,
  ): string[] {

    if (!values?.length) return [];
    if (eventValue === exclusiveValue) {
      return [exclusiveValue];
    }
    return values.filter(v => v !== exclusiveValue);
  }
}
